import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  OpticalAttention,
  OpticalDrilldownKind,
  OpticalDrilldownRow,
  OpticalTurnaroundGroup,
  OpticalMessageState,
  OpticalPipelineStage,
  OpticalReportQuery,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Card } from '@/components/ui/Card';
import { useOpticalRecordsQuery } from '../api/customersOptical.api';
import { ExportCsvButton } from './ExportCsvButton';
import { RecordPager, SimpleTable, CountText, DaysText } from './ReportBlocks';

const PAGE_SIZE = 20;

export interface OpticalRecordsRequest {
  kind: OpticalDrilldownKind;
  status?: OpticalPipelineStage;
  attention?: OpticalAttention;
  lifecycle?: OpticalTurnaroundGroup;
  messageState?: OpticalMessageState;
  /** What the figure is called, already translated. */
  title: string;
}

/**
 * The jobs behind one Optical figure, paged by the server from the statement the figure came from.
 * References, statuses and dates only. A customer column appears only when the server sends names
 * (customers.view), and a message column only when it sends message states.
 */
export function OpticalRecords({
  query,
  request,
  customerIdentity,
  messageState,
  onClose,
}: {
  query: OpticalReportQuery;
  request: OpticalRecordsRequest;
  customerIdentity: boolean;
  messageState: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const count = (value: number) => <CountText value={value} />;
  const days = (seconds: number | null) => <DaysText seconds={seconds} />;
  const [page, setPage] = useState(1);
  const recordsQuery = {
    ...query,
    kind: request.kind,
    status: request.status,
    attention: request.attention,
    lifecycle: request.lifecycle,
    messageState: request.messageState,
    page,
    pageSize: PAGE_SIZE,
  };
  const records = useOpticalRecordsQuery(recordsQuery);
  const pages = records.data ? Math.max(1, Math.ceil(records.data.total / PAGE_SIZE)) : 1;
  const headingId = `optical-records-${request.kind}-${request.status ?? request.attention ?? request.lifecycle ?? 'all'}-${request.messageState ?? ''}`;
  const ltr = (value: ReactNode) => <span dir="ltr">{value ?? '—'}</span>;
  const col = (key: string) => t(`reports.optical.columns.${key}`);
  const withMessages =
    messageState &&
    (request.status === 'READY_FOR_COLLECTION' || request.attention === 'READY_PAST');

  const base = (row: OpticalDrilldownRow): ReactNode[] => [
    ltr(row.reference),
    ltr(row.orderReference),
    row.branchName,
    t(`sales.opticalJobs.status.${row.status}`),
    row.labName ?? t('reports.optical.noLab'),
    ...(customerIdentity ? [row.customerName ?? '—'] : []),
  ];
  const baseHeaders = [
    col('job'),
    col('order'),
    col('branch'),
    col('status'),
    col('lab'),
    ...(customerIdentity ? [col('customer')] : []),
  ];

  const table = (rows: OpticalDrilldownRow[]) =>
    request.kind === 'DELIVERED_JOBS' ? (
      <SimpleTable
        headers={[...baseHeaders, col('deliveredOn'), col('lifecycle'), col('turnaround')]}
        ends={[baseHeaders.length + 2]}
        rows={rows.map((row) => ({
          key: row.id,
          cells: [
            ...base(row),
            ltr(row.businessDate),
            row.lifecycle ? t(`reports.optical.lifecycle.${row.lifecycle}`) : '—',
            days(row.turnaroundSeconds),
          ],
        }))}
      />
    ) : (
      <SimpleTable
        headers={[
          ...baseHeaders,
          col('since'),
          col('ageDays'),
          ...(withMessages ? [col('message')] : []),
        ]}
        ends={[baseHeaders.length + 1]}
        rows={rows.map((row) => ({
          key: row.id,
          cells: [
            ...base(row),
            ltr(row.businessDate),
            row.ageDays === null ? '—' : count(row.ageDays),
            ...(withMessages
              ? [row.messageState ? t(`reports.optical.messageState.${row.messageState}`) : '—']
              : []),
          ],
        }))}
      />
    );

  return (
    <Card className="min-w-0 p-4">
      <section aria-labelledby={headingId}>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h3 id={headingId} className="text-ink font-semibold">
            {request.title}
          </h3>
          <span className="flex flex-wrap items-baseline gap-3">
            <ExportCsvButton
              target={{
                method: 'GET',
                path: '/reports/optical/records/export',
                query: recordsQuery,
              }}
            />
            <button
              type="button"
              onClick={onClose}
              className="text-brand-700 text-sm font-medium hover:underline"
            >
              {t('reports.sales.records.close')}
            </button>
          </span>
        </div>
        {records.isError ? (
          <div className="mt-3">
            <Alert tone="error">{t('reports.sales.records.loadError')}</Alert>
          </div>
        ) : null}
        {records.isPending ? (
          <div
            className="bg-surface border-line mt-3 h-24 animate-pulse rounded-lg border"
            aria-hidden
          />
        ) : null}
        {records.data ? (
          <p className="text-muted mt-2 text-sm">
            {t('reports.optical.hints.jobs', { count: records.data.total })}
          </p>
        ) : null}
        {records.data && records.data.rows.length === 0 ? (
          <p className="text-muted mt-3 text-sm">{t('reports.sales.records.empty')}</p>
        ) : null}
        {records.data && records.data.rows.length > 0 ? table(records.data.rows) : null}
        {records.data ? <RecordPager page={page} pages={pages} onPage={setPage} /> : null}
      </section>
    </Card>
  );
}
